/**
 * LazyDemoSection — defers loading of a heavy, interactive portal demo until the
 * user scrolls it near the viewport.
 *
 * Why: the embedded portal demo (the combined PortalDemo used on the home page,
 * careers page and the client-portal service page) ships a large amount of
 * JavaScript. Statically importing it
 * pulls that code into the initial page/route bundle even for visitors who never
 * scroll down to the demo. This wrapper:
 *
 *   1. Renders a lightweight, static placeholder frame ("Loading interactive
 *      demo…") that costs almost nothing.
 *   2. Uses an IntersectionObserver to detect when the demo's container is about
 *      to enter the viewport, and only THEN dynamically imports the real demo
 *      (its own code-split chunk) and mounts it.
 *   3. Keeps the demo mounted once loaded, so its internal state, fullscreen
 *      mode and guided tour behave exactly as before — nothing about the demo
 *      itself changes, only WHEN it is loaded.
 *
 * The dynamic `load` import is what actually splits each demo out of the route
 * bundle: because the component is never referenced statically, the bundler
 * emits it (and everything it pulls in) as a separate on-demand chunk.
 */
import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type JSX,
} from 'react';

type LazyDemoSectionProps<P = Record<string, never>> = {
  /** Dynamic import of the demo module, e.g. `() => import('./PortalDemo')`. */
  load: () => Promise<{ default: ComponentType<P> }>;
  /** Props forwarded to the loaded demo component. */
  componentProps?: P;
  /** Outer wrapper classes so the placeholder occupies the same box as the demo. */
  className?: string;
  /** Min-height for the placeholder frame to minimise layout shift on mount. */
  minHeight?: string;
  /** Accessible label / caption shown under the spinner. */
  label?: string;
  /** How far before the viewport (px) to begin loading. */
  rootMargin?: string;
};

const BRAND = '#5048E5';

function DemoPlaceholder({
  minHeight,
  label,
}: {
  minHeight: string;
  label: string;
}) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[24px] shadow-2xl"
      style={{
        minHeight,
        border: '1px solid hsl(var(--border))',
        background: 'hsl(var(--card))',
        isolation: 'isolate',
      }}
      role="status"
      aria-live="polite"
    >
      {/* soft brand glow so the frame reads as "the demo, loading" */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 0%, hsla(243,75%,59%,0.10), transparent 60%)',
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <span
          className="h-8 w-8 animate-spin rounded-full border-2 border-b-transparent"
          style={{ borderColor: `${BRAND}`, borderBottomColor: 'transparent' }}
        />
        <p
          className="text-sm font-semibold"
          style={{
            color: 'hsl(var(--muted-foreground))',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          }}
        >
          {label}
        </p>
      </div>
    </div>
  );
}

export default function LazyDemoSection<P = Record<string, never>>({
  load,
  componentProps,
  className = 'w-full',
  minHeight = 'min(760px, 82vh)',
  label = 'Loading interactive demo…',
  rootMargin = '600px 0px',
}: LazyDemoSectionProps<P>) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);


  // Create the lazy component only once the section is in view, so the dynamic
  // import (and its chunk) is not requested until then.
  const LazyComp = useMemo(
    () => (inView ? lazy(load) : null),
    // `load` is a stable per-call-site arrow; we intentionally key only on inView.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inView],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} className={className}>
      {LazyComp ? (
        <Suspense
          fallback={<DemoPlaceholder minHeight={minHeight} label={label} />}
        >
          <LazyComp {...((componentProps ?? {}) as P & JSX.IntrinsicAttributes)} />
        </Suspense>
      ) : (
        <DemoPlaceholder minHeight={minHeight} label={label} />
      )}
    </div>
  );
}
